import {t} from 'i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {withTranslation} from 'react-i18next';
import {withRouter} from 'react-router-dom/cjs/react-router-dom.min';
import {Button, Dialog, Menu} from 'primereact';
import ActionButton from '../../components/ActionButton';
import BaseListContainer from '../../baseContainers/BaseListContainer';
import CriteriaDropdownComponent from '../../components/criteria/CriteriaDropdownComponent';
import CriteriaTextComponent from '../../components/criteria/CriteriaTextComponent';
import YesNoDialog from '../../components/YesNoDialog';
import IssueService from '../../services/IssueService';
import Constants from '../../utils/constants';

const FINAL_STATUSES = ['RESOLVED', 'REJECTED'];

class IssueListContainer extends BaseListContainer {
    constructor(props) {
        super(props, new IssueService());
        this.state = {
            list: [],
            loading: true,
            size: 0,
            first: 0,
            criteria: this.getCleanSearchCriteria(),
            statusOptions: [],
            typeOptions: [],
            selectedIssue: null,
            detailsVisible: false,
            showConfirmResolve: false,
            showConfirmReject: false,
            pendingActionIssue: null,
        };
        this.handleOpenDetails = this.handleOpenDetails.bind(this);
        this.handleCloseDetails = this.handleCloseDetails.bind(this);
        this.handleShowConfirmResolve = this.handleShowConfirmResolve.bind(this);
        this.handleShowConfirmReject = this.handleShowConfirmReject.bind(this);
    }

    getCleanSearchCriteria() {
        return {
            status: null,
            type: null,
            title: '',
            firstResult: 0,
            maxResults: 10,
            sortField: 'createDate',
            sortAsc: false,
        };
    }

    updateSearchCriteria(criteria) {
        return {
            status: criteria.status,
            type: criteria.type,
            title: criteria.title,
            firstResult: criteria.firstResult,
            maxResults: criteria.maxResults,
            sortField: criteria.sortField,
            sortAsc: criteria.sortAsc,
        };
    }

    getDataKey() {
        return 'id';
    }

    getListTitle() {
        return 'issue.listTitle';
    }

    getCriteriaName() {
        return 'issue-list-sc';
    }

    getContainerListName() {
        return 'issue-list-container';
    }

    initializeFromBackend() {
        Promise.all([
            this.enumService.getLocalizedEnumListForLocale('IssueStatus'),
            this.enumService.getLocalizedEnumListForLocale('IssueType'),
        ])
            .then(([statusOptions, typeOptions]) => {
                this.setState({statusOptions, typeOptions});
            })
            .catch(() => {
                this.showErrorMessage(t('issue.error.loadEnums'));
            });
    }

    isFinalStatus(status) {
        return FINAL_STATUSES.includes(status);
    }

    resolveEnumValue(value) {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return value;
        }
        return value.enumValue || value.value || null;
    }

    enumLabel(enumName, value) {
        const enumValue = this.resolveEnumValue(value);
        if (!enumValue) {
            return '';
        }
        const key = `${enumName}.${enumValue}`;
        const translated = t(key);
        return translated !== key ? translated : enumValue;
    }

    statusTemplate(rowData) {
        return this.enumLabel('IssueStatus', rowData.status);
    }

    typeTemplate(rowData) {
        return this.enumLabel('IssueType', rowData.type);
    }

    dateTemplate(field, format, rowData) {
        if (rowData[field] && moment(rowData[field]).isValid()) {
            return moment(rowData[field]).format(format);
        }
        return '';
    }

    renderCriteria() {
        return (
            <div className='row'>
                <CriteriaDropdownComponent
                    id='status-sc'
                    name='status'
                    showClear
                    label={t('issue.status')}
                    colClass='col-lg-3 col-sm-6'
                    value={this.state.criteria.status}
                    options={this.state.statusOptions}
                    placeholder={t('list.all')}
                    onChange={this.handleChangeSc}
                    validator={this.validator}
                    optionLabel='label'
                    refreshFromBackend
                />
                <CriteriaDropdownComponent
                    id='type-sc'
                    name='type'
                    showClear
                    label={t('issue.type')}
                    colClass='col-lg-3 col-sm-6'
                    value={this.state.criteria.type}
                    options={this.state.typeOptions}
                    placeholder={t('list.all')}
                    onChange={this.handleChangeSc}
                    validator={this.validator}
                    optionLabel='label'
                    refreshFromBackend
                />
                <CriteriaTextComponent
                    id='title-sc'
                    name='title'
                    label={t('issue.title')}
                    colClass='col-lg-6 col-sm-12'
                    value={this.state.criteria.title}
                    onChange={this.handleChangeSc}
                    validator={this.validator}
                    refreshFromBackend
                    timeoutHandler={this.refreshTable}
                />
            </div>
        );
    }

    prepareColumns() {
        return [
            {field: 'id', header: 'ID', sortable: true, width: '80px'},
            {
                field: 'createDate',
                header: t('issue.createDate'),
                sortable: true,
                body: this.dateTemplate.bind(this, 'createDate', Constants.DATE_TIME_FORMAT),
            },
            {field: 'type', header: t('issue.type'), sortable: true, body: this.typeTemplate.bind(this)},
            {field: 'status', header: t('issue.status'), sortable: true, body: this.statusTemplate.bind(this)},
            {field: 'title', header: t('issue.title'), sortable: true},
            {field: 'companyName', header: t('issue.company'), sortable: false},
            {field: 'userName', header: t('issue.user'), sortable: false},
            {field: 'userEmail', header: t('user.email'), sortable: false},
            {field: 'action', header: t('global.action'), body: this.actionTemplate, float: 'center'},
        ];
    }

    handleOpenDetails(rowData) {
        this.setState({
            selectedIssue: rowData,
            detailsVisible: true,
        });
    }

    handleCloseDetails() {
        this.setState({
            selectedIssue: null,
            detailsVisible: false,
        });
    }

    handleShowConfirmResolve(issue) {
        this.setState({
            pendingActionIssue: issue,
            showConfirmResolve: true,
        });
    }

    handleShowConfirmReject(issue) {
        this.setState({
            pendingActionIssue: issue,
            showConfirmReject: true,
        });
    }

    onMarkResolved(callback) {
        const issue = this.state.pendingActionIssue;
        if (!issue) {
            return;
        }
        this.blockUi();
        this.service
            .markResolved(issue.id)
            .then(() => {
                this.showSuccessMessage(t('issue.resolveSuccess'));
                this.handleCloseDetails();
                if (callback) {
                    callback();
                }
                this.refreshTable();
            })
            .catch(() => {
                this.showErrorMessage(t('issue.actionError'));
            })
            .finally(() => {
                this.unblockUi();
            });
    }

    onMarkRejected(callback) {
        const issue = this.state.pendingActionIssue;
        if (!issue) {
            return;
        }
        this.blockUi();
        this.service
            .markRejected(issue.id)
            .then(() => {
                this.showSuccessMessage(t('issue.rejectSuccess'));
                this.handleCloseDetails();
                if (callback) {
                    callback();
                }
                this.refreshTable();
            })
            .catch(() => {
                this.showErrorMessage(t('issue.actionError'));
            })
            .finally(() => {
                this.unblockUi();
            });
    }

    actionTemplate(rowData) {
        const menuRef = React.createRef();
        const canChangeStatus = !this.isFinalStatus(rowData.status);

        const menuItems = [
            {
                label: t('global.details'),
                icon: 'pi pi-eye',
                command: () => this.handleOpenDetails(rowData),
            },
            canChangeStatus && {
                label: t('issue.markResolved'),
                icon: 'pi pi-check',
                command: () => this.handleShowConfirmResolve(rowData),
            },
            canChangeStatus && {
                label: t('issue.markRejected'),
                icon: 'pi pi-times',
                command: () => this.handleShowConfirmReject(rowData),
            },
        ].filter(Boolean);

        return (
            <div>
                <Menu model={menuItems} popup ref={menuRef} />
                <Button
                    icon='pi pi-ellipsis-v'
                    className='menu-items'
                    onClick={(event) => menuRef.current.toggle(event)}
                />
            </div>
        );
    }

    renderDetailsDialog() {
        const issue = this.state.selectedIssue;
        if (!issue) {
            return null;
        }
        const canChangeStatus = !this.isFinalStatus(issue.status);

        return (
            <Dialog
                header={t('issue.details')}
                visible={this.state.detailsVisible}
                style={{width: '50vw'}}
                onHide={this.handleCloseDetails}
                dismissableMask
            >
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.type')}</div>
                    <div className='col-8'>{this.typeTemplate(issue)}</div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.status')}</div>
                    <div className='col-8'>{this.statusTemplate(issue)}</div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.createDate')}</div>
                    <div className='col-8'>
                        {this.dateTemplate('createDate', Constants.DATE_TIME_FORMAT, issue)}
                    </div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.title')}</div>
                    <div className='col-8'>{issue.title}</div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.detailsLabel')}</div>
                    <div className='col-8' style={{whiteSpace: 'pre-wrap'}}>
                        {issue.details}
                    </div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.company')}</div>
                    <div className='col-8'>{issue.companyName}</div>
                </div>
                <div className='row mb-2'>
                    <div className='col-4 font-weight-bold'>{t('issue.user')}</div>
                    <div className='col-8'>
                        {issue.userName} ({issue.userEmail})
                    </div>
                </div>
                {canChangeStatus ? (
                    <React.Fragment>
                        <hr className='line-separator' style={{marginTop: '24px', marginBottom: '24px'}} />
                        <div className='issue-details-dialog-footer'>
                            <ActionButton
                                label={t('issue.markResolved')}
                                variant='button save-button blue'
                                iconName='mdi-check'
                                iconSide='left'
                                handleClick={() => this.handleShowConfirmResolve(issue)}
                            />
                            <ActionButton
                                label={t('issue.markRejected')}
                                variant='button cancel-button gray'
                                iconName='mdi-close'
                                iconSide='left'
                                handleClick={() => this.handleShowConfirmReject(issue)}
                            />
                        </div>
                    </React.Fragment>
                ) : null}
            </Dialog>
        );
    }

    renderConfirmDialogs() {
        return (
            <React.Fragment>
                {this.state.showConfirmResolve ? (
                    <YesNoDialog
                        onChange={(type, x, target) => {
                            if (target.value) {
                                this.onMarkResolved(() => {
                                    this.setState({showConfirmResolve: false, pendingActionIssue: null});
                                });
                            } else {
                                this.setState({showConfirmResolve: false, pendingActionIssue: null});
                            }
                        }}
                        visible={this.state.showConfirmResolve}
                        header={t('issue.resolveHeader')}
                        name='visible'
                        className='issue-confirm-dialog'
                        noButtonVariant='button cancel-button gray'
                        yesButtonVariant='button save-button blue'
                        onHide={() => this.setState({showConfirmResolve: false, pendingActionIssue: null})}
                    >
                        {t('issue.resolveMessage')}
                    </YesNoDialog>
                ) : null}
                {this.state.showConfirmReject ? (
                    <YesNoDialog
                        onChange={(type, x, target) => {
                            if (target.value) {
                                this.onMarkRejected(() => {
                                    this.setState({showConfirmReject: false, pendingActionIssue: null});
                                });
                            } else {
                                this.setState({showConfirmReject: false, pendingActionIssue: null});
                            }
                        }}
                        visible={this.state.showConfirmReject}
                        header={t('issue.rejectHeader')}
                        name='visible'
                        className='issue-confirm-dialog'
                        noButtonVariant='button cancel-button gray'
                        yesButtonVariant='button save-button blue'
                        onHide={() => this.setState({showConfirmReject: false, pendingActionIssue: null})}
                    >
                        {t('issue.rejectMessage')}
                    </YesNoDialog>
                ) : null}
            </React.Fragment>
        );
    }

    breadCrumbItems() {
        return [{name: t('issue.listName'), url: '/issue-list'}];
    }

    render() {
        return (
            <div className='container-fluid'>
                {this.renderConfirmDialogs()}
                {this.renderDetailsDialog()}
                {this.renderView()}
            </div>
        );
    }
}

IssueListContainer.defaultProps = {
    lazy: true,
};

IssueListContainer.propTypes = {};

export default withRouter(withTranslation()(IssueListContainer));
